import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { currentIdToken } from '../auth/cognito';
import { useGameSocket } from '../ws/useGameSocket';
import { useGameStore } from '../store';
import { useCountdown } from '../hooks/useCountdown';
import { Action } from '../shared/protocol';
import { JoinQR } from '../components/JoinQR';
import { useT } from '../i18n';

// Seconds relative to the tier reference (first correct buzz), e.g. "+1.2s".
const formatDelta = (ms: number) => `${ms > 0 ? '+' : ''}${(ms / 1000).toFixed(1)}s`;

export function AdminConsole() {
  const t = useT();
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    void currentIdToken().then((t) => {
      if (!t) navigate('/admin', { replace: true });
      else setToken(t);
    });
  }, [navigate]);

  const socket = useGameSocket(token && gameId ? { role: 'admin', token, gameId } : null);
  const { connected, state, calledOn, lastResult, error } = useGameStore();
  const open = state?.questionState === 'open';
  const remaining = useCountdown(open ? state?.endsAt : undefined);

  const [showQR, setShowQR] = useState(true);
  const [copied, setCopied] = useState(false);

  const send = (action: string, data?: unknown) => socket?.send(action, data);

  const joinLink = state ? `${window.location.origin}/play/${state.code}` : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is shown next to the button anyway */
    }
  };

  return (
    <div className="stack">
      <div className="rowbetween">
        <div className="row">
          <Link to="/admin/games" className="ghost">{t('console.backGames')}</Link>
          {state && <span className="pill"><strong>{state.code}</strong></span>}
        </div>
        <span className={connected ? 'status ok' : 'status bad'}>{connected ? t('console.live') : t('console.connecting')}</span>
      </div>

      {error && <div className="banner error">{t(error)}</div>}
      {!state && <div className="card centered"><p className="muted">{t('console.loading')}</p></div>}

      {state && state.status !== 'finished' && (
        <div className="card">
          <div className="sharehead">
            <a href={joinLink} className="joinlink" title={joinLink}>{joinLink.replace(/^https?:\/\//, '')}</a>
            <span className="sharebtns">
              <button className="ghost small nowrap" onClick={copyLink}>{copied ? t('console.copied') : t('console.copyLink')}</button>
              <button className="ghost small nowrap" onClick={() => setShowQR((v) => !v)}>{showQR ? t('console.hideQR') : t('console.showQR')}</button>
            </span>
          </div>
          {showQR && <JoinQR code={state.code} />}
        </div>
      )}

      {state && (
        <>
          {state.status === 'lobby' && (
            <div className="card centered">
              <h2>{t('console.waitingPlayers')}</h2>
              <p className="muted">{t('console.pressStart')}</p>
            </div>
          )}

          <div className="card">
            <div className="rowbetween">
              <div className="muted topic-title">{state.topicTitle ?? '—'}</div>
              <div className="muted small">{t('console.stateLabel', { status: t(`status.${state.status}`), qstate: t(`qstate.${state.questionState}`) })}</div>
            </div>

            {state.question ? (
              <>
                <h2 className="question">{state.question.text}</h2>
                <div className="rowbetween">
                  <span className="points">{t('play.pts', { n: state.question.points })}</span>
                  {open && <span className="timer">{remaining}s</span>}
                </div>
                {state.answerNote && <div className="answer">{t('console.answer', { answer: state.answerNote })}</div>}
              </>
            ) : (
              <p className="muted">{t('console.noQuestion')}</p>
            )}

            <div className="controls">
              {state.status === 'lobby' && (
                <button className="primary" onClick={() => send(Action.AdminStart)}>{t('console.startGame')}</button>
              )}
              {state.status === 'active' && (
                <>
                  {/* Buzzer only opens for a fresh (idle) question. Once it has run
                      or been answered, it must be explicitly reset to run again. */}
                  {state.question && state.questionState === 'idle' && (
                    <button className="primary" onClick={() => send(Action.AdminOpenQ)}>
                      {t('console.openBuzzer')}
                    </button>
                  )}
                  {state.question && state.questionState !== 'idle' && (
                    <button className="primary" onClick={() => send(Action.AdminResetQ)}>
                      {t('console.resetBuzzer')}
                    </button>
                  )}
                  <span className="navgroup">
                    <button className="ghost" title={t('nav.prevTopic')} onClick={() => send(Action.AdminPrevTop)}>◀◀</button>
                    <button className="ghost" title={t('nav.prevQuestion')} onClick={() => send(Action.AdminPrevQ)}>◀</button>
                    <button className="ghost" title={t('nav.nextQuestion')} onClick={() => send(Action.AdminNextQ)}>▶</button>
                    <button className="ghost" title={t('nav.nextTopic')} onClick={() => send(Action.AdminNextTop)}>▶▶</button>
                  </span>
                  <button className="danger endbtn" onClick={() => { if (confirm(t('console.endGameConfirm'))) send(Action.AdminEndGame); }}>
                    {t('console.endGame')}
                  </button>
                </>
              )}
              {state.status === 'finished' && <p className="muted">{t('console.gameFinished')}</p>}
            </div>
          </div>

          {state.status === 'active' && (
            <div className="card">
              <h3>{t('console.buzzedIn', { n: state.queue?.length ?? 0 })}</h3>
              {(!state.queue || state.queue.length === 0) ? (
                <p className="muted">{t('console.noBuzzes')}</p>
              ) : (
                <ol className="buzzqueue">
                  {state.queue.map((e) => (
                    <li key={e.playerId} className={`res-${e.result}`}>
                      <span className="qname">
                        {calledOn?.playerId === e.playerId && '📣 '}
                        {e.nickname}
                        {(e.deltaMs ?? 0) !== 0 && <span className="muted small"> {formatDelta(e.deltaMs)}</span>}
                        {e.result !== 'pending' && (
                          <em> — {t(`res.${e.result}`)}{e.result === 'correct' && (e.pointsAwarded ?? 0) > 0 ? ` (+${e.pointsAwarded})` : ''}</em>
                        )}
                      </span>
                      <span className="row">
                        {e.result === 'pending' ? (
                          <>
                            <button className="ghost" onClick={() => send(Action.AdminCallOn, { playerId: e.playerId })}>{t('console.callOn')}</button>
                            <button
                              className="ok"
                              disabled={(e.pointsOnCorrect ?? 0) === 0}
                              title={(e.pointsOnCorrect ?? 0) === 0 ? t('console.tooLate') : undefined}
                              onClick={() => send(Action.AdminMark, { playerId: e.playerId, correct: true })}
                            >
                              ✓{(e.pointsOnCorrect ?? 0) > 0 ? ` +${e.pointsOnCorrect}` : ''}
                            </button>
                            <button className="danger" onClick={() => send(Action.AdminMark, { playerId: e.playerId, correct: false })}>✗</button>
                          </>
                        ) : (
                          <button className="ghost" onClick={() => send(Action.AdminUnmark, { playerId: e.playerId })}>↩︎ {t('console.undo')}</button>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
              {lastResult && (
                <div className={`banner ${lastResult.correct ? 'success' : 'error'}`}>
                  {lastResult.nickname}: {lastResult.correct ? t('console.correct', { points: lastResult.points }) : t('console.wrong')}
                </div>
              )}
            </div>
          )}

          <div className="card">
            <h3>{t('console.scores')}</h3>
            <ul className="scores">
              {(state.scores ?? []).map((s) => (
                <li key={s.playerId}><span>{s.nickname}</span><span className="points">{s.score}</span></li>
              ))}
              {(state.scores ?? []).length === 0 && <li className="muted">{t('console.noPlayers')}</li>}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
